// Turf: six grass lanes with mowing stripes and a worn running strip, plus the surrounding
// ground. Colour comes from a world-space shader, so 600 m of track never shows a repeat.
// Each lane has its own material with a `tripIntensity` uniform for the catnip trip job.
import {
  Group, Mesh, MeshStandardMaterial, PlaneGeometry, RepeatWrapping, Uniform, Vector2,
  type CanvasTexture,
} from 'three';
import { LANE_COUNT, LANE_WIDTH, TRACK_LENGTH, laneCenterZ } from '../../data/track';
import { COURSE, LAKE, TRACK_HALF_WIDTH } from './layout';
import { GLSL_FLOOD_REACH, SEASON_UNIFORMS } from './seasonUniforms';
import { GLSL_NOISE, GLSL_TURF, canvasTexture, rng } from './util';

/** Uniforms the trip job drives per lane. */
export interface LaneUniforms {
  tripIntensity: Uniform<number>;
  tripTime: Uniform<number>;
}

/** A small tiling grass-detail normal map (blade streaks), generated once. */
function grassNormal(): CanvasTexture {
  const r = rng(11);
  const t = canvasTexture(512, 512, (g, w, h) => {
    // Height field of short streaks, then converted to a normal map.
    const hc = document.createElement('canvas'); hc.width = w; hc.height = h;
    const hg = hc.getContext('2d')!;
    hg.fillStyle = '#808080'; hg.fillRect(0, 0, w, h);
    for (let i = 0; i < 9000; i++) {
      const x = r() * w, y = r() * h, l = 4 + r() * 10, a = (r() - 0.5) * 0.9;
      const v = Math.floor(90 + r() * 120);
      hg.strokeStyle = `rgb(${v},${v},${v})`;
      hg.lineWidth = 1 + r() * 1.5;
      hg.beginPath(); hg.moveTo(x, y); hg.lineTo(x + Math.sin(a) * l, y - Math.cos(a) * l); hg.stroke();
    }
    const src = hg.getImageData(0, 0, w, h).data;
    const out = g.createImageData(w, h);
    const H = (x: number, y: number) => src[(((y + h) % h) * w + ((x + w) % w)) * 4]! / 255;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const dx = (H(x + 1, y) - H(x - 1, y)) * 2.2, dy = (H(x, y + 1) - H(x, y - 1)) * 2.2;
      const len = Math.hypot(dx, dy, 1);
      const i = (y * w + x) * 4;
      out.data[i] = (-dx / len * 0.5 + 0.5) * 255;
      out.data[i + 1] = (dy / len * 0.5 + 0.5) * 255;
      out.data[i + 2] = (1 / len * 0.5 + 0.5) * 255;
      out.data[i + 3] = 255;
    }
    g.putImageData(out, 0, 0);
  }, { data: true, repeat: true });
  t.wrapS = t.wrapT = RepeatWrapping;
  return t;
}

/** Patches MeshStandardMaterial with procedural turf colour in world space. */
function turfMaterial(normal: CanvasTexture, kind: 'lane' | 'ground', lane: number): { material: MeshStandardMaterial; uniforms: LaneUniforms } {
  const uniforms: LaneUniforms = { tripIntensity: new Uniform(0), tripTime: new Uniform(0) };
  const m = new MeshStandardMaterial({ roughness: 0.93, metalness: 0, normalMap: normal, normalScale: new Vector2(0.55, 0.55) });
  m.name = kind === 'lane' ? `turf-lane-${lane}` : 'turf-ground';
  if (kind === 'lane') { m.polygonOffset = true; m.polygonOffsetFactor = -1; m.polygonOffsetUnits = -2; }
  const laneZ = kind === 'lane' ? laneCenterZ(lane) : 0;
  m.onBeforeCompile = (shader) => {
    shader.uniforms.tripIntensity = uniforms.tripIntensity;
    shader.uniforms.tripTime = uniforms.tripTime;
    shader.uniforms.uSnow = SEASON_UNIFORMS.uSnow;
    shader.uniforms.uDryGrass = SEASON_UNIFORMS.uDryGrass;
    shader.uniforms.uFlood = SEASON_UNIFORMS.uFlood;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vTurfPos;')
      .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvTurfPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
varying vec3 vTurfPos;
uniform float tripIntensity;
uniform float tripTime;
${GLSL_NOISE}
${GLSL_TURF}
${GLSL_FLOOD_REACH}
const float LANE_Z = ${laneZ.toFixed(3)};
const float IS_LANE = ${kind === 'lane' ? '1.0' : '0.0'};
const float TRACK_LEN = ${TRACK_LENGTH.toFixed(1)};
const float HALF_W = ${TRACK_HALF_WIDTH.toFixed(3)};
const float SHORE_Z = ${LAKE.shoreZ.toFixed(1)};
`)
      .replace('#include <map_fragment>', /* glsl */ `
vec2 wp = vTurfPos.xz;
// Large patches of healthier and drier grass.
float big = envFbm(wp * 0.035);
float mid = envFbm(wp * 0.22 + 3.1);
float fine = envNoise(wp * 2.7);
vec3 col = turfColour(mid, big);
col *= 0.86 + fine * 0.22;
float roughAdd = 0.0;
if (IS_LANE > 0.5) {
  // Mowing stripes across the lanes, 6 m wide.
  float stripe = step(0.5, fract(wp.x / 12.0));
  col *= mix(0.88, 1.1, stripe);
  // Worn running strip down the middle of the lane, heavier near the start and the finish.
  float across = abs(wp.y - LANE_Z);
  float wearAlong = 0.45 + 0.55 * max(smoothstep(60.0, 0.0, wp.x), smoothstep(TRACK_LEN - 90.0, TRACK_LEN, wp.x));
  // Kept narrow and light so the lanes still read as grass from the overview cameras.
  float worn = (1.0 - smoothstep(0.15, 0.55, across + (mid - 0.5) * 0.4)) * wearAlong;
  vec3 dirt = vec3(0.26, 0.20, 0.11) * (0.8 + fine * 0.4);
  col = mix(col, mix(col, dirt, 0.65), worn * 0.45);
  roughAdd = worn * 0.05;
  // Darker grass tucked under the edging boards (contact occlusion).
  float edge = smoothstep(0.9, 1.2, across);
  col *= 1.0 - edge * 0.35;
} else {
  // Outside the track: longer, darker meadow; a trodden apron along the hoardings.
  float apron = 1.0 - smoothstep(HALF_W + 0.5, HALF_W + 3.5, abs(wp.y));
  col = mix(col, col * vec3(1.05, 1.0, 0.85), apron * 0.4);
  col *= 0.92;
  // Sandy shore and reeds' darker band towards the lake.
  float shore = smoothstep(SHORE_Z - 8.0, SHORE_Z - 1.0, wp.y);
  col = mix(col, vec3(0.42, 0.35, 0.22), shore * 0.8);
}
// Winter: snow over the meadow, trodden thinner on the apron; the lanes are kept clear, with a
// dusting along the edging and in patches, so the cats still read against the turf.
if (uSnow > 0.0) {
  float drift = envFbm(wp * 0.09 + 11.0);
  vec3 snowCol = vec3(0.8, 0.83, 0.88) * (0.9 + fine * 0.1) * mix(0.92, 1.04, drift);
  float cover;
  if (IS_LANE > 0.5) {
    float across = abs(wp.y - LANE_Z);
    cover = 0.08 + 0.45 * smoothstep(0.75, 1.15, across) + 0.12 * smoothstep(0.6, 0.85, mid);
  } else {
    float apron = 1.0 - smoothstep(HALF_W + 0.5, HALF_W + 3.5, abs(wp.y));
    cover = 1.0 - apron * 0.35 * (0.6 + fine * 0.4);
  }
  float s = clamp(cover * smoothstep(0.0, 1.0, uSnow * (0.75 + drift * 0.5)), 0.0, 1.0);
  col = mix(col, snowCol, s);
  roughAdd -= s * 0.2;
}
// Catnip trip hook: a domain-warped red, cyan and lime swirl the trip job can drive.
if (tripIntensity > 0.0) {
  vec2 q = wp * 0.35 + vec2(envFbm(wp * 0.2 + tripTime * 0.3), envFbm(wp * 0.2 - tripTime * 0.25)) * 3.0;
  float k = sin(q.x * 2.0 + tripTime * 3.0) * 0.5 + 0.5;
  float k2 = sin(q.y * 2.3 - tripTime * 2.4) * 0.5 + 0.5;
  vec3 psy = mix(mix(vec3(1.0, 0.1, 0.2), vec3(0.0, 0.9, 1.0), k), vec3(0.5, 1.0, 0.1), k2 * 0.6);
  col = mix(col, psy, tripIntensity);
}
diffuseColor.rgb *= col * floodReach(vTurfPos);
`)
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nroughnessFactor = clamp(roughnessFactor + roughAdd, 0.0, 1.0);')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += col * tripIntensity * 1.6;');
  };
  m.customProgramCacheKey = () => `turf-${kind}-${lane}`;
  return { material: m, uniforms };
}

export class Lanes {
  readonly group = new Group();
  /** One entry per lane (0..5): the material and its trip uniforms. */
  readonly lanes: { mesh: Mesh; material: MeshStandardMaterial; uniforms: LaneUniforms }[] = [];
  readonly ground: Mesh;

  constructor() {
    this.group.name = 'turf';
    const normal = grassNormal();
    normal.repeat.set(1, 1);
    const length = COURSE.to - COURSE.from;
    for (let lane = 0; lane < LANE_COUNT; lane++) {
      const { material, uniforms } = turfMaterial(normal, 'lane', lane);
      // UVs repeat the detail normal every 1.6 m.
      const geo = new PlaneGeometry(length, LANE_WIDTH, 1, 1);
      const uv = geo.getAttribute('uv');
      for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * length / 1.6, uv.getY(i) * LANE_WIDTH / 1.6);
      const mesh = new Mesh(geo, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set((COURSE.from + COURSE.to) / 2, 0.002, laneCenterZ(lane));
      mesh.receiveShadow = true;
      mesh.name = `lane-${lane}`;
      this.group.add(mesh);
      this.lanes.push({ mesh, material, uniforms });
    }
    // Ground: a large field from the forest to the lake shore.
    const gw = 2400, gzFrom = -700, gzTo = LAKE.shoreZ + 4;
    const { material } = turfMaterial(normal, 'ground', -1);
    const geo = new PlaneGeometry(gw, gzTo - gzFrom, 1, 1);
    const uv = geo.getAttribute('uv');
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * gw / 1.6, uv.getY(i) * (gzTo - gzFrom) / 1.6);
    this.ground = new Mesh(geo, material);
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.position.set(TRACK_LENGTH / 2, 0, (gzFrom + gzTo) / 2);
    this.ground.receiveShadow = true;
    this.ground.name = 'ground';
    this.group.add(this.ground);
  }

  update(timeS: number): void {
    for (const l of this.lanes) if (l.uniforms.tripIntensity.value > 0) l.uniforms.tripTime.value = timeS;
  }
}
